import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Logo } from "@/components/brand/Logo";
import { api, errMsg } from "@/lib/api";
import { openGoogleAuth } from "@/lib/googleAuth";
import { OBJECTIVES } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const Q = ["Qual é o seu negócio?", "O que você vende?", "Em qual região você atende?", "Qual seu objetivo?", "Conecte seu Google Ads.", "Vamos criar sua primeira campanha."];

export default function Onboarding() {
  const { orgInfo, reload, reloadOrg } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [p, setP] = useState({ name: orgInfo?.org?.name || "", site: "", category: "", services: "", city: "", state: "", objective: "leads", ...(orgInfo?.org?.business_profile || {}) });
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });

  const save = async (done, to) => {
    try {
      await api.put("/org", { business_profile: p });
      if (done) { await api.put("/me", { onboarding_done: true }); await reload(); await reloadOrg(); }
      if (to) nav(done && localStorage.getItem("cz_pending_plan") ? "/app/assinatura" : to);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const connect = async () => {
    await save(false);
    try { await openGoogleAuth(); } catch (e) { toast.error(errMsg(e)); }
  };
  const canNext = [p.name.length > 1, p.services.trim().length > 2, p.city.length > 1, !!p.objective, true, true][step];

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 h-16 flex items-center"><Logo to="#" /><span className="ml-auto font-mono text-xs text-muted-foreground" data-testid="onboarding-progress-label">{step + 1}/6</span></header>
      <Progress value={((step + 1) / 6) * 100} className="h-1 rounded-none" />
      <main className="flex-1 flex items-center justify-center p-6">
        <div key={step} className="w-full max-w-xl reveal">
          <p className="eyebrow mb-3">Etapa {step + 1} de 6</p>
          <h1 className="text-3xl sm:text-4xl font-semibold mb-8">{Q[step]}</h1>
          {step === 0 && <div className="space-y-3">
            <Input data-testid="onboarding-business-name" placeholder="Nome do negócio" value={p.name} onChange={set("name")} className="h-12" />
            <Input data-testid="onboarding-business-site" placeholder="Site (https://...)" value={p.site} onChange={set("site")} className="h-12" />
            <Input data-testid="onboarding-business-category" placeholder="Categoria (ex: assistência técnica)" value={p.category} onChange={set("category")} className="h-12" />
          </div>}
          {step === 1 && <Textarea data-testid="onboarding-services" rows={5} placeholder={"Um serviço ou produto por linha\nex: Conserto de geladeira\nManutenção de ar-condicionado"} value={p.services} onChange={set("services")} />}
          {step === 2 && <div className="grid grid-cols-[1fr_120px] gap-3">
            <Input data-testid="onboarding-city" placeholder="Cidade" value={p.city} onChange={set("city")} className="h-12" />
            <Input data-testid="onboarding-state" placeholder="UF" value={p.state} onChange={set("state")} className="h-12" />
          </div>}
          {step === 3 && <div className="grid grid-cols-2 gap-3">{OBJECTIVES.map((o) => (
            <button key={o.id} data-testid={`onboarding-objective-${o.id}`} onClick={() => setP({ ...p, objective: o.id })}
              className={`h-14 rounded-xl border text-sm font-medium transition-colors ${p.objective === o.id ? "bg-primary text-primary-foreground border-foreground" : "hover:bg-secondary"}`}>{o.label}</button>))}</div>}
          {step === 4 && <div className="space-y-4">
            <p className="text-muted-foreground">Autorize o acesso pela tela oficial do Google. Seus tokens ficam criptografados no servidor e nunca no navegador.</p>
            <Button data-testid="onboarding-connect-google" onClick={connect} className="rounded-full h-12 px-6">Conectar Google Ads</Button>
          </div>}
          {step === 5 && <p className="text-muted-foreground">Vamos usar as informações que você preencheu para montar a primeira estrutura com IA. Você revisa tudo antes de publicar.</p>}
          <div className="flex justify-between mt-10">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="onboarding-back-button"><ArrowLeft className="h-4 w-4 mr-1" />Voltar</Button>
            {step < 5 ? (
              <Button disabled={!canNext} onClick={() => setStep(step + 1)} className="rounded-full px-6" data-testid="onboarding-next-button">{step === 4 ? "Pular por agora" : "Continuar"}<ArrowRight className="h-4 w-4 ml-1" /></Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" className="rounded-full" onClick={() => save(true, "/app")} data-testid="onboarding-skip-button">Ir ao dashboard</Button>
                <Button className="rounded-full" onClick={() => save(true, "/app/campaigns/new")} data-testid="onboarding-finish-button">Criar campanha</Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
